import type { RootState } from "../state";
import { ElMessage } from "element-plus";
import { switchProducts } from "@/api/live";
import { useLiveRoomStore } from "@/store/modules/liveRoom";
import { createAudioControllerFromRoot } from "./audioController";
import { $t, transformI18n } from "@/plugins/i18n";
// Web（非苹果）专用：选择产品并处理切换
/**
 *
 * @param this 当前store
 * @param params carousel 是否直播
 * @param id 产品id
 * @param index 产品轮播Index
 * @returns
 */
export async function selectProductWeb(
  this: RootState & { [key: string]: any },
  params: { id: string; index: number; carousel?: boolean }
) {
  if (!this.currentRoomState) return;
  if (this.currentRoomState.startLiveLoading) {
    return ElMessage.error(
      transformI18n("msg.liveStartingCannotSwitchProduct")
    );
  }
  const state = this.currentRoomState;
  const { id, index } = params;

  if (this.currentRoomState.insertSegmentList.length > 0) {
    ElMessage.warning(
      transformI18n("msg.currentProductHasUnfinishedInsertedScript")
    );
    return;
  }

  if (state.selectedProductId === id) {
    ElMessage.warning(transformI18n("msg.currentProductPlayingNoRepeatSwitch"));
    return;
  }
  state.cardLoadingMap[id] = true;

  // 仅基于 Howler 播放器判断播放态（不涉及苹果专用分支）
  const isCurrentlyPlaying = !!(
    state.isPlaying && (state as any).universalWebAudioPlayer
  );

  const roomId = state.roomId || state.productList?.id || this.currentRoomId;
  if (!roomId) {
    ElMessage.error(transformI18n("msg.switchProductFailedCannotGetRoomId"));
    state.cardLoadingMap[id] = false;
    return;
  }

  const switchParams = {
    roomId,
    productId: state.productList.products[index].id
  };

  // 进入新切换版本：重置版本号为1，确保每次切换产品后版本号从1开始，避免累积导致的版本跳跃
  // 这样每次产品切换后，新版本音频的version都是1，不会出现0->2的跳跃
  state.switchVersion = 1;

  // 有音频在播：先调用 API，设置 pending，等当前条结束后在完成回调里执行

  if (isCurrentlyPlaying) {
    try {
      console.log(`[web-switch] select pending set -> pid=${id} idx=${index}`);
    } catch {}
    try {
      const res = await switchProducts(switchParams);
      if (res && res.success) {
        const audioController = createAudioControllerFromRoot(this, roomId);
        // 有当前控制器 并且 不是轮播的时候直接切换 否则需要续播
        console.log("params.carousel", params.carousel);

        if (audioController && params.carousel !== true) {
          // 停止当前播放 清空队列还有相关id映射
          audioController.stop();
          audioController.clearQueue();
          audioController.clearInternalCollections();
        }
        // 防护极端时机：设置pending后再次检查播放状态
        const isStillPlaying = !!(
          state.isPlaying && (state as any).howlerPlayer
        );
        const howlerStatus = (state as any).howlerPlayer?.getStatus?.();
        const reallyPlaying = isStillPlaying && howlerStatus?.isPlaying;
        if (!reallyPlaying) {
          // 🚨 极端时机检测：音频已经在API调用期间结束，直接执行切换
          try {
            console.log(
              `[web-switch] 极端时机检测：音频已结束，直接切换 pid=${id}`
            );
          } catch {}

          // 直接更新状态，不设置pending
          state.selectedProductId = id;
          state.productDetail = state.productList.products[index];
          state.lastProductSwitchTime = Date.now();
          state.switchingProductId = id;
          // 清空队列
          // state.clearQueue(roomId);
          // 关键修复：清除队列中所有音频的时间进度，防止新产品从中间播放
          state.queue.forEach(item => {
            delete (item as any).audioTimeProgress;
            delete (item as any).audioCurrentTime;
            delete (item as any).lastSavedProgress;
            item.playedCharCount = 0;
            item.status = "pending";
          });

          // 立即触发播放下一条
          setTimeout(() => {
            state.cardLoadingMap[id] = false;
            this.playNext(roomId);
          }, 60);
          // 改为在“可听”时再提示成功
          try {
            (state as any).pendingSwitchSuccessMessage = true;
          } catch {}
          // loading 交由“可听”事件统一关闭
          return;
        }

        // 边界切换：不销毁 Howler，保留已预加载的下一条，等边界执行
        state.pendingProductSwitch = {
          productId: id,
          productIndex: index,
          switchParams,
          apiCompleted: true,
          createdAt: Date.now() // 记录创建时间用于超时检测
        };
        try {
          console.log(`[web-switch] api ok, pending established pid=${id}`);
        } catch {}
      } else {
        ElMessage.error(
          `${transformI18n("msg.switchProductFailed")}: ${res?.message || "未知错误"}`
        );
        state.cardLoadingMap[id] = false;
      }
    } catch (e: any) {
      ElMessage.error(
        `${transformI18n("msg.switchProductFailed")}: ${e?.message || e}`
      );
      state.cardLoadingMap[id] = false;
    }
    return;
  }

  // 无在播音频：直接执行切换
  if (state.productList.status === "live") {
    try {
      const res = await switchProducts(switchParams);
      state.cardLoadingMap[id] = false;
      // stop() 方法会完全清空队列、重置索引并停止播放
      const audioController = createAudioControllerFromRoot(this, roomId);
      if (audioController) {
        // 停止当前播放 清空队列还有相关id映射
        audioController.stop();
        audioController.clearQueue();
        audioController.clearInternalCollections();
      }
      // if (state && (state as any).universalWebAudioPlayer) {
      //   state.queue = [];
      //   (state as any).universalWebAudioPlayer.stop();
      // }
      if (res && res.success) {
        // 直接切换（未在播）：此处可以清理播放器。但为了最大化复用预加载，尽量不销毁。
        // 仅在确有需要时再清理（此处先不清理）。

        // 更新所选产品
        state.selectedProductId = id;
        state.productDetail = state.productList.products[index];

        // 设置切换标记，用于过滤旧产品音频
        state.lastProductSwitchTime = Date.now();
        state.switchingProductId = id;

        // 关键修复：清除队列中所有音频的时间进度，防止新产品从中间播放
        state.queue.forEach(item => {
          delete (item as any).audioTimeProgress;
          delete (item as any).audioCurrentTime;
          delete (item as any).lastSavedProgress;
          item.playedCharCount = 0;
          item.status = "pending";
        });

        // 扫描首个可播项并启动播放（不重置队列进度，复用已预加载 Howl）
        const PROTECT_MS = Number((state as any).switchProtectMs ?? 1000);
        const inProtect =
          !!state.lastProductSwitchTime &&
          Date.now() - state.lastProductSwitchTime <= PROTECT_MS;
        let probeIndex = 0;
        while (probeIndex < state.queue.length) {
          const q: any = state.queue[probeIndex];
          const currentPid =
            state.switchingProductId || state.selectedProductId || null;
          const sameProduct = !!(
            q?.productId &&
            currentPid &&
            q.productId === currentPid
          );
          const verMismatchRaw = q?.version !== (state.switchVersion ?? 0);
          const verMismatch = verMismatchRaw && !sameProduct;
          const wrongProduct =
            state.switchingProductId &&
            q?.productId &&
            q.productId !== state.switchingProductId;
          const tooOldInProtect =
            inProtect &&
            !q?.productId &&
            q?.createTime &&
            q.createTime < state.lastProductSwitchTime!;
          const noUrl = !q?.audioUrl;
          if (verMismatch || wrongProduct || tooOldInProtect || noUrl) {
            probeIndex++;
            continue;
          }
          break;
        }
        if (probeIndex < state.queue.length) {
          state.currentIndex = probeIndex;
          state.isPlaying = false; // 让 playNext 统一起播
          state.cardLoadingMap[id] = false;
          setTimeout(() => {
            this.playNext(roomId);
          }, 60);
        }

        // 改为在“可听”时再提示成功
        try {
          (state as any).pendingSwitchSuccessMessage = true;
        } catch {}
      } else {
        ElMessage.error(
          `${transformI18n("msg.switchProductFailed")}: ${res?.message || "未知错误"}`
        );
      }
    } catch (e: any) {
      ElMessage.error(
        `${transformI18n("msg.switchProductFailed")}: ${e?.message || e}`
      );
      // 失败时关闭 loading
      state.cardLoadingMap[id] = false;
    }
  } else {
    // 未开播：仅更新 UI 选中
    state.selectedProductId = id;
    state.productDetail = state.productList.products[index] || {};
    state.cardLoadingMap[id] = false;
  }
}

// Web（非苹果）专用：执行 pending 切换（在播放完成边界触发）
export async function executePendingProductSwitchWeb(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state || !state.pendingProductSwitch) return;

  const { productId, productIndex, switchParams, apiCompleted } =
    state.pendingProductSwitch;
  state.pendingProductSwitch = null;

  try {
    let res: any = { success: true };
    if (!apiCompleted) res = await switchProducts(switchParams);
    if (res && res.success) {
      // 保留 Howler 播放器与已预加载资源，只有状态切换
      state.selectedProductId = productId;
      state.productDetail = state.productList.products[productIndex];

      // 切换标记，用于过滤旧产品音频
      state.lastProductSwitchTime = Date.now();
      state.switchingProductId = productId;

      // 关键修复：清除队列中所有音频的时间进度，防止新产品从中间播放
      state.queue.forEach(item => {
        delete (item as any).audioTimeProgress;
        delete (item as any).audioCurrentTime;
        delete (item as any).lastSavedProgress;
        item.playedCharCount = 0;
        item.status = "pending";
      });

      // 直接触发下一条选择（playNext 内部做可播过滤）
      setTimeout(() => {
        // 🔧 关键修复：在触发playNext之前清除所有音频时间进度
        try {
          state.queue.forEach(item => {
            delete (item as any).audioTimeProgress;
            delete (item as any).audioCurrentTime;
            delete (item as any).lastSavedProgress;
          });
          console.log(`🔧 产品切换前已清除所有音频时间进度，确保从头播放`);
        } catch (e) {
          console.error("清除音频进度时出错:", e);
        }

        // 防死锁：强制清除等待进度恢复标志
        try {
          const howlerPlayer = (state as any).howlerPlayer;
          if (
            howlerPlayer &&
            typeof howlerPlayer.setWaitingForProgressRestore === "function"
          ) {
            howlerPlayer.setWaitingForProgressRestore(false);
          }
        } catch {}
        this.playNext(roomId);
      }, 60);
      // 改为在“可听”时再提示成功，且统一在监听里关闭 loading
      try {
        (state as any).pendingSwitchSuccessMessage = true;
      } catch {}
      try {
        if (productId && state.cardLoadingMap)
          state.cardLoadingMap[productId] = state.cardLoadingMap[productId];
      } catch {}
    } else {
      ElMessage.error(
        `${transformI18n("msg.switchProductFailed")}: ${res?.message || "未知错误"}`
      );
    }
  } catch (e: any) {
    ElMessage.error(
      `${transformI18n("msg.switchProductFailed")}: ${e?.message || e}`
    );
  }
}

// 自动选择（苹果用原切换，其它走 Web 专用）
export async function selectProductAuto(
  this: RootState & { [key: string]: any },
  params: { id: string; index: number }
) {
  try {
    const ua = navigator.userAgent || "";
    const isIOS =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
    const isSafari = /Safari/i.test(ua) && !/Chrome|Chromium|Edge/i.test(ua);
    const appleLike = isIOS || isSafari;
    if (appleLike && typeof (this as any).selectProduct === "function") {
      return (this as any).selectProduct(params);
    }
    return (this as any).selectProductWeb(params);
  } catch {
    return (this as any).selectProductWeb(params);
  }
}
